// 방식 C(한 파일에 여러 답안) 나누기 확인 단계: 쪽 빠짐 계산과 '위와 같은 학생'(위 칸에 이어 붙이기).

const CONFIDENCE_RANK = { low: 0, medium: 1, high: 2 };

const toPageNumber = (value) => {
  const page = Number(value);
  return Number.isInteger(page) && page > 0 ? page : null;
};

export const collectSplitPages = (students = []) => {
  const pages = new Set();
  (students || []).forEach((student) => {
    (student?.source_pages || []).forEach((value) => {
      const page = toPageNumber(value);
      if (page) pages.add(page);
    });
  });
  return [...pages].sort((a, b) => a - b);
};

// 처음 나눴을 때 누군가에게 있던 쪽 가운데 지금 어느 학생에게도 없는 쪽
export const findDroppedSplitPages = (initialPages = [], students = []) => {
  const current = new Set(collectSplitPages(students));
  return [...new Set((initialPages || []).map(toPageNumber).filter(Boolean))]
    .filter(page => !current.has(page))
    .sort((a, b) => a - b);
};

export const formatPageList = (pages = []) => {
  const sorted = [...new Set((pages || []).map(toPageNumber).filter(Boolean))].sort((a, b) => a - b);
  const parts = [];
  for (let i = 0; i < sorted.length; i += 1) {
    let end = i;
    while (end + 1 < sorted.length && sorted[end + 1] === sorted[end] + 1) end += 1;
    parts.push(end > i ? `${sorted[i]}-${sorted[end]}` : `${sorted[i]}`);
    i = end;
  }
  return parts.join(', ');
};

const firstPage = (student) => collectSplitPages([student])[0] ?? Number.POSITIVE_INFINITY;

const unique = (items) => [...new Set(items.filter(Boolean))];

const lowerConfidence = (a, b) => {
  if (!(a in CONFIDENCE_RANK)) return b;
  if (!(b in CONFIDENCE_RANK)) return a;
  return CONFIDENCE_RANK[a] <= CONFIDENCE_RANK[b] ? a : b;
};

// 지운 학생들을 쪽 순서에 맞는 자리로 되돌린다. 쪽 정보가 없으면 맨 뒤에 둔다.
export const restoreSplitStudentsByPage = (students = [], removed = []) => {
  const next = [...(students || [])];
  [...(removed || [])]
    .filter(Boolean)
    .sort((a, b) => firstPage(a) - firstPage(b))
    .forEach((student) => {
      const page = firstPage(student);
      const position = next.findIndex(item => firstPage(item) > page);
      next.splice(position < 0 ? next.length : position, 0, student);
    });
  return next;
};

// index 학생의 답안을 바로 위(index - 1) 학생에게 붙인다. 이름은 위 학생 것을 쓰고, 답안은 쪽 순서대로 잇는다.
export const mergeSplitStudentIntoPrevious = (students = [], index, { buildExcerpt, buildFilename } = {}) => {
  if (!Array.isArray(students) || index < 1 || index >= students.length) return null;
  const previous = students[index - 1];
  const current = students[index];
  const ordered = firstPage(current) < firstPage(previous) ? [current, previous] : [previous, current];
  const answerText = ordered.map(student => (student.answer_text || '').trim()).filter(Boolean).join('\n\n');
  const keepPreviousName = Boolean((previous.name || '').trim()) || !(current.name || '').trim();
  const nameOwner = keepPreviousName ? previous : current;
  const sourcePages = collectSplitPages([previous, current]);
  const merged = {
    ...previous,
    name: nameOwner.name,
    name_source: nameOwner.name_source,
    answer_text: answerText,
    answer_excerpt: buildExcerpt ? buildExcerpt(answerText) : answerText.slice(0, 120),
    source_pages: sourcePages,
    split_confidence: lowerConfidence(previous.split_confidence, current.split_confidence),
    split_warnings: unique([...(previous.split_warnings || []), ...(current.split_warnings || [])]),
    merged_split_ids: unique([
      ...(previous.merged_split_ids || [previous.split_student_id]),
      ...(current.merged_split_ids || [current.split_student_id]),
    ]),
    _recognitionEdited: true,
  };
  if (buildFilename) merged.filename = buildFilename(merged, index - 1);
  const next = [...students];
  next.splice(index - 1, 2, merged);
  return { students: next, merged, previous, absorbed: current };
};
